import React, { useCallback, useState } from 'react';
import { InputField, SelectField } from '@wk/components-react16';
import { PasswordField, PasswordFieldMode } from '@wk/components-v3-react16';
import { FieldValues, UseFormRegister, useFormContext } from 'react-hook-form';
import styles from '../CreateProductParams.module.scss';
import { IWorkflowProduct } from 'interfaces/runOrchestration/workflowRow';
import { MODAL_NAMES } from 'components/Modals/modal-list';

interface Parameter {
  maxLength?: number;
  name: string;
  options?: string[];
  paramId: keyof IWorkflowProduct;
  required?: boolean;
  type: 'text' | 'select' | 'password';
}

interface FormFieldProperties {
  clearErrors: (name: string) => void;
  modalName: string | null;
  onFieldChange: (id: string, value: string) => void;
  param: Parameter;
  register: UseFormRegister<FieldValues>;
  selectedOrchestration: IWorkflowProduct | null;
  setError: (name: string, error: { message: string; type: string }) => void;
}

function FormField({
  param,
  register,
  modalName,
  selectedOrchestration,
  onFieldChange,
}: FormFieldProperties) {
  const { formState: { errors } } = useFormContext();
  const [passwordMode, setPasswordMode] =
    useState<PasswordFieldMode>('password');

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { id, value } = event.target;
      onFieldChange(id, value);
    },
    [onFieldChange]
  );

  const { required, paramId } = param;
  const indicator = required ? 'required' : 'none';
  const isEditMode = modalName === MODAL_NAMES.EDIT_PRODUCT;
  const existingValue = selectedOrchestration?.[paramId]?.toString();
  const fieldError = errors[paramId];

  if (param.type === 'select') {
    return (
      <SelectField label={param.name} labelFor={paramId} indicator={indicator}>
        <select
          id={paramId}
          data-testid={`Select-${paramId}`}
          {...register(paramId)}
          onChange={handleChange}
          defaultValue={isEditMode ? existingValue : ''}
          required={required}
        >
          <option value="false">No</option>
          <option value="true">Yes</option>
        </select>
      </SelectField>
    );
  }

  if (param.type === 'password') {
    const passwordErrors: string[] =
      fieldError?.message && typeof fieldError.message === 'string'
        ? [fieldError.message]
        : [];

    return (
      <PasswordField
        label={param.name}
        labelFor={paramId}
        indicator={indicator}
        mode={passwordMode}
        errors={passwordErrors}
        onModeChange={({ mode }) => setPasswordMode(mode)}
        flowClass={styles['cg-field']}
      >
        <input
          type={passwordMode}
          id={paramId}
          data-testid={`Input-${paramId}`}
          {...register(paramId)}
          onChange={handleChange}
          defaultValue={isEditMode ? existingValue : undefined}
          required={required}
        />
      </PasswordField>
    );
  }

  return (
    <InputField
      label={param.name}
      labelFor={paramId}
      errors={
        fieldError?.message && typeof fieldError.message === 'string'
          ? [{ label: fieldError.message, labelId: paramId }]
          : undefined
      }
      indicator={indicator}
    >
      <input
        type={param.type}
        id={paramId}
        data-testid={`Input-${paramId}`}
        {...register(paramId)}
        onChange={handleChange}
        defaultValue={isEditMode ? existingValue : undefined}
        required={required}
      />
    </InputField>
  );
}

export default React.memo(FormField);
